import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiService } from '../../api.service';
import { SkeletonModule } from 'primeng/skeleton';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { Socket } from 'ngx-socket-io';
import { TooltipModule } from 'primeng/tooltip';
import { PrinterService } from '../../printer.service';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ButtonModule } from 'primeng/button';
import { TAGInvItemGroup, TAGInvItemType, TAGInvTransaction } from '../../../models/taginventory';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { findInvByCode } from './inventoryHelper';
import {
  TransactionListDialog,
  TransactionListDialogData,
} from './transaction-list-dialog/transactionListDialog';
import { dialogConfig } from '../../dialogs';
import {
  BarcodeScannerSearchField,
  CodeScannedEvent,
} from '../../components/barcode-scanner-search-field/barcode-scanner-search-field';
import { TableModule } from 'primeng/table';
import {
  TransactionEditDialog,
  TransactionEditDialogData,
} from './transaction-edit-dialog/transactionEditDialog';
import {
  MissingItemsDialog,
  MissingItemsDialogData,
} from './missing-items-dialog/missingItemsDialog';
import { GroupListDialog, GroupListDialogData } from './group-list-dialog/groupListDialog';
import { GroupEditDialog, GroupEditDialogData } from './group-edit-dialog/groupEditDialog';
import { ItemEditDialog, ItemEditDialogData } from './item-edit-dialog/itemEditDialog';
import {
  TransactionCreateDialog,
  TransactionCreateDialogData,
} from './transaction-create-dialog/transaction-create-dialog';
import {
  InventoryCheckDialog,
  InventoryCheckDialogData,
} from './inventory-check-dialog/inventory-check-dialog';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../components/progressDialog/progressDialog';
import { EmptyStateComponent } from '../../components/empty-state/empty-state.component';

@Component({
  selector: 'app-internal-inventory',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    SkeletonModule,
    TranslatePipe,
    MatIcon,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    ButtonModule,
    CheckboxModule,
    FormsModule,
    BarcodeScannerSearchField,
    TableModule,
    EmptyStateComponent,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-inventory.html',
  styleUrl: './internal-inventory.scss',
})
export class InternalInventory {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  private socket = inject(Socket);
  public api = inject(ApiService);
  public printer = inject(PrinterService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  categories = signal<string[]>([]);
  items = signal<TAGInvItemType[]>([]);
  loadingItems = signal(true);
  groups = signal<TAGInvItemGroup[]>([]);
  //loadingGroups = signal(true);
  transactions = signal<TAGInvTransaction[]>([]);
  //loadingTransactions = signal(true);

  filteredItems = computed(() => {
    var items = this.items().filter(
      (item) =>
        (item.category == this.selectedCategory() || this.selectedCategory() == undefined) &&
        (this.searchFilter() == '' ||
          item.name.toLowerCase().includes(this.searchFilter().toLowerCase())),
    );
    return items.filter((item) => !item.deleted);
  });
  selectedItems = signal<string[]>([]);

  selectedCategory = signal<string>(undefined);

  searchFilter = signal('');

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.loadInventory();

      this.socket.on('loginInv', (data) => {
        if (!data.success) {
          console.error('Failed to login to inventory socket: ' + data.message);
        } else {
          this.initSocketEvents();
        }
      });

      this.socket.emit('loginInv', { token: this.api.token });
    });
  }

  ngAfterViewInit() {
    this.search?.reset();
  }

  ngOnDestroy(): void {
    this.socket.removeListener('createItem', this.socket_onCreateItem.bind(this));
    this.socket.removeListener('updateItem', this.socket_onUpdateItem.bind(this));
    this.socket.removeListener('deleteItem', this.socket_onDeleteItem.bind(this));
  }

  initSocketEvents() {
    this.socket.on('createItem', this.socket_onCreateItem.bind(this));
    this.socket.on('updateItem', this.socket_onUpdateItem.bind(this));
    this.socket.on('deleteItem', this.socket_onDeleteItem.bind(this));
  }

  socket_onCreateItem(data: any) {
    this.items.update((i) => {
      i.push(new TAGInvItemType(data.item));
      return [...i];
    });
    this.extractCategories();
  }

  socket_onUpdateItem(data: any) {
    this.items.update((i) => {
      i.splice(
        i.findIndex((item) => item._id == data.item._id),
        1,
        new TAGInvItemType(data.item),
      );
      return [...i];
    });
    this.extractCategories();
  }

  socket_onDeleteItem(data: any) {
    this.items.update((i) => {
      i.filter((item) => item._id != data.item._id);
      return [...i];
    });
    this.extractCategories();
  }

  printerSupported(): boolean {
    return this.printer.isSupported();
  }

  printerConnected(): boolean {
    return this.printer.isConnected();
  }

  printerPrinting(): boolean {
    return this.printer.isPrintingLabels();
  }

  connectPrinter() {
    this.printer.connectToPrinter();
  }

  countCategory(category: string): number {
    return this.filteredItems().filter((item) => item.category == category).length;
  }

  loadInventory() {
    this.categories.set(Array.from({ length: 3 }).map((_, i) => 'LEEEEEEL'));
    this.items.set(Array.from({ length: 3 }).map((_, i) => new TAGInvItemType()));

    this.loadingItems.set(true);

    this.api
      .getInvItems()
      .then((invItems: TAGInvItemType[]) => {
        this.items.set(invItems);
        this.extractCategories();
        this.loadingItems.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryItems')(),
          detail: reason.message,
        });
      });

    this.api
      .getInvItemGroups()
      .then((invGroups: TAGInvItemGroup[]) => {
        this.groups.set(invGroups);
        //this.loadingGroups.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryGroups')(),
          detail: reason.message,
        });
      });

    this.api
      .getInvTransactions()
      .then((invTransactions: TAGInvTransaction[]) => {
        this.transactions.set(invTransactions);
        //this.loadingTransactions.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
          detail: reason.message,
        });
      });
  }

  extractCategories() {
    let categories = [];
    this.items()
      .filter((item) => !item.deleted)
      .forEach((item) => {
        if (!categories.includes(item.category)) {
          categories.push(item.category);
        }
      });

    categories.sort();

    if (!categories.includes(this.selectedCategory())) {
      //this.selectCategory(this.categories[0]);
      this.selectCategory(undefined);
    }

    console.log(categories);
    this.categories.set(categories);
  }

  selectCategory(category?: string) {
    this.selectedCategory.set(category);
    this.selectedItems.set([]);
    this.search?.reset();
  }

  selectItem(item: TAGInvItemType) {
    this.selectedItems.update((i) => {
      if (i.includes(item._id)) i.splice(i.indexOf(item._id), 1);
      else i.push(item._id);

      return [...i];
    });
  }

  async codeScanned(code: CodeScannedEvent) {
    let { item, instance, group, transaction } = await findInvByCode(
      code.code,
      this.items(),
      this.groups(),
      this.transactions(),
    );

    if (item) {
      this.openItem(item, [instance.itemid]);
    } else if (group) {
      this.showGroup(group);
    } else if (transaction) {
      this.showTransaction(transaction);
    } else {
      console.log('Unknown code: ' + code.code);
      return;
    }

    this.search?.reset();
  }

  openItem(item: TAGInvItemType, selectedInstances?: string[]) {
    const dialogRef = this.dialogService.open(
      ItemEditDialog,
      dialogConfig<ItemEditDialogData>({
        data: {
          item: item,
          selectedInstances: selectedInstances,
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  openCreateTransactionDialog() {
    const dialogRef = this.dialogService.open(
      TransactionCreateDialog,
      dialogConfig<TransactionCreateDialogData>({
        closable: true,
        data: {},
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.api
          .createTransaction(result)
          .then((transaction) => {
            this.showTransaction(transaction);
          })
          .catch((reason) => {
            console.error(reason);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.translate('errors.couldNotCreateTransaction')(),
              detail: reason.message,
            });
          });
      }

      this.search?.reset();
    });
  }

  startInventoryCheck() {
    const dialogRef = this.dialogService.open(
      InventoryCheckDialog,
      dialogConfig<InventoryCheckDialogData>({
        closable: true,
        data: {},
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.showTransaction(result._id);
      }

      this.search?.reset();
    });
  }

  showTransaction(transaction: TAGInvTransaction) {
    const dialogRef = this.dialogService.open(
      TransactionEditDialog,
      dialogConfig<TransactionEditDialogData>({
        closable: true,
        data: {
          transactionId: transaction._id,
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  showTransactions() {
    const dialogRef = this.dialogService.open(
      TransactionListDialog,
      dialogConfig<TransactionListDialogData>({
        dismissableMask: true,
        closable: true,
        data: {
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  showMissingItems() {
    const dialogRef = this.dialogService.open(
      MissingItemsDialog,
      dialogConfig<MissingItemsDialogData>({
        dismissableMask: true,
        closable: true,
        data: {
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  showGroups() {
    const dialogRef = this.dialogService.open(
      GroupListDialog,
      dialogConfig<GroupListDialogData>({
        dismissableMask: true,
        closable: true,
        data: {
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  showGroup(group: TAGInvItemGroup) {
    const dialogRef = this.dialogService.open(
      GroupEditDialog,
      dialogConfig<GroupEditDialogData>({
        closable: true,
        data: {
          group: group,
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  newItem(bulk: boolean = false, lastCategory: string = undefined) {
    const dialogRef = this.dialogService.open(
      ItemEditDialog,
      dialogConfig<ItemEditDialogData>({
        closable: true,
        data: {
          item: new TAGInvItemType({ category: (lastCategory ?? this.selectedCategory()) ?? undefined }),
          socket: this.socket,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      this.loadInventory();
      if (bulk && result) {
        this.newItem(true, result.category);
      }
      this.search?.reset();
    });
  }

  deleteSelected() {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.inventory.deleteMultipleTitle', {
          amount: this.selectedItems().length,
        })(),
        this.translate.translate('internal.inventory.deleteMultipleMessage', {
          amount: this.selectedItems().length,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.inventory.deleteMultipleProgress')(),
          )
            .setShowProgress(true)
            .setProgress(0),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          let deleted = 0;
          for (let item of this.selectedItems()) {
            try {
              await this.api.deleteInvItem(item);
              deleted++;
              deleteStep.update((s) => s.setProgress(deleted / this.selectedItems().length));
            } catch (reason) {
              deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
              deleteStep.update((s) => s.setSubtitle(reason.message));
              await pDialog.finishDialog(-1);
              console.log(reason);
              this.loadInventory();
              return;
            }
          }

          deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
          this.selectedItems.set([]);
          await pDialog.finishDialog();
          this.loadInventory();
        });
      }
    });
  }
}
